import {
    GetRosterSearchesQuery,
    RosterStatus,
    RosterType,
} from '__generated__/graphql'
import ClientIdentityProvider from 'client/contexts/ClientIdentityContext'
import { GetRosterSearches } from 'client/graphql/search.gql'
import { useIdentity } from 'client/hooks'
import { clientRouteConfig } from 'client/shared/routeTargets'
import RostersByStatusList from 'shared/components/RostersByStatusList'
import { usePagination } from 'shared/hooks/usePagination'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { GroupedStatus, RosterResults } from 'shared/models/groupedStatus'
import { ClientVisibleProcessStatusGroups } from './CandidateList'
import ClientRosterCardPreview, {
    RosterListProps,
} from './ClientRosterCardPreview'
import { DetailsRowProps } from './DetailsRow'

const defaultPageLimit = 10
const savedFilterToken = 'client.searches.activeFilter'
const trackedClassName = 'roster-search-preview'

const RosterSearches: React.FC = () => {
    usePageTitle('Searches')
    const identity = useIdentity()

    const activeRosterPagination = usePagination<
        GetRosterSearchesQuery['rosterSearches']['data'][number]
    >({
        query: GetRosterSearches,
        variables: {
            rosterStatuses: [RosterStatus.Active],
            limit: defaultPageLimit,
        },
        queryPath: 'rosterSearches',
        limit: defaultPageLimit,
        trackedDivClassName: `${trackedClassName}-active`,
    })

    const closedRosterPagination = usePagination<
        GetRosterSearchesQuery['rosterSearches']['data'][number]
    >({
        query: GetRosterSearches,
        variables: {
            rosterStatuses: [RosterStatus.Closed],
            limit: defaultPageLimit,
        },
        queryPath: 'rosterSearches',
        limit: defaultPageLimit,
        trackedDivClassName: `${trackedClassName}-closed`,
    })

    const getPaginationForFilter = (filter: GroupedStatus) => {
        switch (filter.status) {
            case RosterStatus.Closed:
                return {
                    ...closedRosterPagination,
                    trackedDivClassName:
                        closedRosterPagination.trackedDivClassName ||
                        trackedClassName,
                }
            case RosterStatus.Active:
            default:
                return {
                    ...activeRosterPagination,
                    trackedDivClassName:
                        activeRosterPagination.trackedDivClassName ||
                        trackedClassName,
                }
        }
    }

    const getCards = (data: RosterResults[number]) => {
        return (
            <ClientRosterCardPreview
                {...getRostersByStatusSearchPreviewProps(data)}
            />
        )
    }

    return (
        <ClientIdentityProvider>
            <RostersByStatusList
                statuses={[
                    {
                        status: RosterStatus.Active,
                    },
                    {
                        status: RosterStatus.Closed,
                    },
                ]}
                pageTitle={identity?.client?.company?.value ?? ''}
                getPaginationForFilter={getPaginationForFilter}
                saveFilterKey={savedFilterToken}
            >
                {getCards}
            </RostersByStatusList>
        </ClientIdentityProvider>
    )
}

export const getRostersByStatusSearchPreviewProps = (
    rosterData:
        | GetRosterSearchesQuery['rosterSearches']['data'][number]
        | undefined
): RosterListProps => {
    const details: DetailsRowProps = {
        sharedRecruiters:
            rosterData?.sharedRecruiters?.map(({ recruiter }) => ({
                id: recruiter?.id || '',
                preferredName: recruiter?.user?.preferredName || '',
                profileImageKey: recruiter?.user?.profileImageKey || '',
            })) || [],
        sharedClients:
            rosterData?.sharedClients?.map(({ client }) => ({
                id: client?.id || '',
                preferredName: client?.user?.preferredName || '',
                profileImageKey: client?.user?.profileImageKey || '',
            })) || [],
        positions:
            rosterData?.placements?.map(
                (placement) => placement?.title?.value ?? ''
            ) || [],
        startDateLabels:
            rosterData?.startDates?.map((sd) => sd.startDateLabel) || [],
        officeLocations:
            rosterData?.companyOffices?.map(
                (office) => office?.companyOffice?.officeLocation?.value ?? ''
            ) || [],
        industries: rosterData?.industries?.map(
            (industry) => industry?.industry?.value ?? ''
        ),
        sectors: rosterData?.sectors?.map(
            (sector) => sector?.sector?.value ?? ''
        ),
        hideOnShrink: true,
    }
    return {
        details,
        rosterName: rosterData?.rosterName ?? '',
        rosterType: rosterData?.rosterType ?? RosterType.Search,
        navigationTarget: `${clientRouteConfig.searches.path}`,
        rosterId: rosterData?.id ?? '',
        statusGroupsForGraph: ClientVisibleProcessStatusGroups,
        isSelectable: rosterData?.status === RosterStatus.Active,
        showRosterType: false,
    }
}

export default RosterSearches
